import {
  Button,
  FlexBox,
  FormField,
  FormGrid,
  Page,
  Spinner,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { mimChatbotsApi } from 'apis/chatbots';
import { usersApi } from 'apis/users';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { MimUserAvatarControl } from '../../components/mimUserAvatarControl/mimUserAvatarControl';

const formId = 'mim-chatbot-identity';

type FieldValues = {
  name: string;
  userName: string;
  userAvatarUrl: string;
};

export const MimChatbotIdentityView = (): JSX.Element => {
  const { id } = useAppPathParams('/settings/chatbots/mim/:id/identity');

  const { chatbot, isLoading: isChatbotLoading } =
    mimChatbotsApi.useReadMimChatbotByIdQuery(
      { id },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ data, ...rest }) => {
          return {
            ...rest,
            chatbot: data?.data,
          };
        },
      },
    );

  const { userData, isLoading: isUserLoading } = usersApi.useReadUserQuery(
    { id: chatbot?.systemUserId ?? '' },
    {
      skip: !chatbot?.systemUserId,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => {
        return {
          ...rest,
          userData: data?.data,
        };
      },
    },
  );

  const [updateChatbot, { isLoading: isChatbotUpdating }] =
    mimChatbotsApi.useUpdateMimChatbotIdentityMutation();

  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const navigate = useAppNavigate();
  const notify = useNotifications();

  const setFormValues = (userData, chatbot) => ({
    name: chatbot?.name || '',
    userName: userData?.name || '',
    userAvatarUrl: userData?.avatar_url || '',
  });

  const form = useForm<FieldValues>({
    defaultValues: setFormValues(userData, chatbot),
    mode: 'all',
  });
  const { reset, formState, handleSubmit, control } = form;

  const isPristine = formState.isSubmitted && !formState.isDirty;

  const isLoading = isChatbotLoading || isUserLoading;
  const isLoadingOrValidating = isChatbotUpdating || isLoading;

  useEffect(() => {
    reset(setFormValues(userData, chatbot));
  }, [reset, userData, chatbot]);

  useEffect(() => {
    ampli.chatbotEditIdentityModalOpened();
  }, []);

  const onSubmit: SubmitHandler<FieldValues> = async ({
    name,
    userName,
    userAvatarUrl,
  }) => {
    try {
      const response = await updateChatbot({
        id: id ?? '',
        name,
        user: {
          name: userName,
          ...(userAvatarUrl !== '' ? { avatarUrl: userAvatarUrl } : {}),
        },
      }).unwrap();

      if (isResponseWithData(response)) {
        notify(
          {
            title: 'Success',
            text: `"${response.data?.name || 'Chatbot'}" has been updated.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );
        ampli.chatbotIdentityEdited();
        navigate({ path: '/settings/chatbots/mim' });
      }
    } catch (error) {
      notify(
        {
          title: 'Error while updating chatbot.',
          icon: 'exclamation',
          className: 'danger',
          time: 5000,
        },
        NotificationType.Error,
      );
    }
  };

  const handleOnCancel = () => {
    navigate({ path: '/settings/chatbots/mim' });
  };

  return (
    <Page>
      <Page.Section
        title="Chatbot identity"
        controls={
          <>
            <Button
              variant="secondary"
              type="button"
              disabled={isPristine || isLoadingOrValidating}
              onClick={handleOnCancel}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isPristine || isLoadingOrValidating}
              isLoading={isLoadingOrValidating}
              loadingIndicator={<Spinner size={16} />}
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>
          </>
        }
      >
        <form
          id={formId}
          noValidate
          tabIndex={-1}
          onSubmit={handleSubmit(onSubmit)}
        >
          {isLoading ? (
            <FlexBox justifyContent="center" alignItems="center" height="100px">
              <Spinner size={32} />
            </FlexBox>
          ) : (
            <FormGrid>
              <FormField>
                <FormProvider {...form}>
                  <MimUserAvatarControl />
                </FormProvider>
              </FormField>

              <FormField span={isSmallScreen ? 6 : 3}>
                <HookFormText
                  name="name"
                  label="Internal reference"
                  control={control}
                  rules={{
                    required: 'Internal reference is required',
                  }}
                  helpIcon={{
                    text: 'The reference of your bot for internal purposes.',
                  }}
                />
              </FormField>

              <FormField span={isSmallScreen ? 6 : 3}>
                <HookFormText
                  name="userName"
                  label="Public display name"
                  control={control}
                  rules={{
                    required: 'Public display name is required',
                  }}
                  helpIcon={{
                    text: 'The name that your users will see while interacting with the bot.',
                  }}
                />
              </FormField>
            </FormGrid>
          )}
        </form>
      </Page.Section>
    </Page>
  );
};
